import {
  useGetValuesNotesLazyQuery,
  useGetValuesPlacePostsLazyQuery,
  useGetValuesPlacesLazyQuery,
  useGetValuesRequiredToolsLazyQuery,
  useGetValuesServicesLazyQuery,
  useGetValuesToolsInsuranceLazyQuery,
  useGetValuesToolsLazyQuery,
  useGetValuesToolsMetrologicalLazyQuery,
  useGetValuesTradePremisesLazyQuery,
  useGetValuesWarehousesLazyQuery,
} from "~/schemas/generated/graphql";
import { useEffect, useState } from "react";
import { useZD } from "~/entity/ZD/hooks/useZD";
import { get } from "lodash";

interface IUseZdRequestsProps {
  selectedService: string;
  currentDealerId: string;
  page: number;
  count: number;
  dateBegin: string;
}

export const useZdRequests = ({
  currentDealerId,
  page,
  count,
  dateBegin,
  selectedService,
}: IUseZdRequestsProps) => {
  const defaultStateVal = {
    loading: false,
    items: [],
    total: 0,
    refetch: () => {},
  };

  const [getValuesServices, valuesServicesData] = useGetValuesServicesLazyQuery(
    {
      variables: {
        DealerId: currentDealerId,
        page,
        count,
        dateUpTo: dateBegin ? dateBegin : undefined,
      },
      fetchPolicy: "no-cache",
    }
  );

  const [getValuesRequiredTools, valuesRequiredTools] =
    useGetValuesRequiredToolsLazyQuery({
      variables: {
        DealerId: currentDealerId,
        page,
        count,
        dateUpTo: dateBegin ? dateBegin : undefined,
      },
      fetchPolicy: "no-cache",
    });

  const [getValuesToolsInsurance, valuesToolsInsurance] =
    useGetValuesToolsInsuranceLazyQuery({
      variables: {
        DealerId: currentDealerId,
        page,
        count,
        dateUpTo: dateBegin ? dateBegin : undefined,
      },
      fetchPolicy: "no-cache",
    });

  const [getValuesToolsMetrological, valuesToolsMetrological] =
    useGetValuesToolsMetrologicalLazyQuery({
      variables: {
        DealerId: currentDealerId,
        page,
        count,
        dateUpTo: dateBegin ? dateBegin : undefined,
      },
      fetchPolicy: "no-cache",
    });

  const [getValuesTools, valuesTools] = useGetValuesToolsLazyQuery({
    variables: {
      DealerId: currentDealerId,
      page,
      count,
      dateUpTo: dateBegin ? dateBegin : undefined,
    },
    fetchPolicy: "no-cache",
  });

  const [getValuesWarehouses, valuesWarehouses] =
    useGetValuesWarehousesLazyQuery({
      variables: {
        DealerId: currentDealerId,
        page,
        count,
        dateUpTo: dateBegin ? dateBegin : undefined,
      },
      fetchPolicy: "no-cache",
    });

  const [getValuesPlaces, valuesPlaces] = useGetValuesPlacesLazyQuery({
    variables: {
      DealerId: currentDealerId,
      page,
      count,
      dateUpTo: dateBegin ? dateBegin : undefined,
    },
    fetchPolicy: "no-cache",
  });

  const [getValuesPlacePosts, valuesPlacePosts] =
    useGetValuesPlacePostsLazyQuery({
      variables: {
        DealerId: currentDealerId,
        page,
        count,
        dateUpTo: dateBegin ? dateBegin : undefined,
      },
      fetchPolicy: "no-cache",
    });

  const [getValuesNotes, valuesNotes] = useGetValuesNotesLazyQuery({
    variables: {
      DealerId: currentDealerId,
      page,
      count,
      dateUpTo: dateBegin ? dateBegin : undefined,
    },
    fetchPolicy: "no-cache",
  });

  const [getValuesTradePremises, valuesTradePremises] =
    useGetValuesTradePremisesLazyQuery({
      variables: {
        DealerId: currentDealerId,
        page,
        count,
        dateUpTo: dateBegin ? dateBegin : undefined,
      },
      fetchPolicy: "no-cache",
    });

  const [ZDCurrentState, setZDCurrentState] = useState(null);
  const { getZDLists } = useZD();

  const selectAndInvokeGetter = async (selectedService: string) => {
    const params = {
      dealerId: currentDealerId,
      page,
      count,
    };

    const getter = getZDLists[selectedService];
    if (typeof getter === "function") {
      const { data = {} } = await getter(params);
      return {
        items: data.items,
        total: data.total ?? 0,
        loading: false,
        refetch: () =>
          selectAndInvokeGetter(selectedService).then(setZDCurrentState),
      };
    }

    return defaultStateVal;
  };

  const requests = {
    personal: {
      getter: () => {
        setZDCurrentState({ ...defaultStateVal, loading: true });
        selectAndInvokeGetter(selectedService).then(setZDCurrentState);
      },
      data: ZDCurrentState,
    },
    services: {
      getter: getValuesServices,
      data: valuesServicesData,
    },
    requiredTools: {
      getter: getValuesRequiredTools,
      data: valuesRequiredTools,
    },
    toolsInsurance: {
      getter: getValuesToolsInsurance,
      data: valuesToolsInsurance,
    },
    toolsMetrological: {
      getter: getValuesToolsMetrological,
      data: valuesToolsMetrological,
    },
    tools: {
      getter: getValuesTools,
      data: valuesTools,
    },
    warehouses: {
      getter: getValuesWarehouses,
      data: valuesWarehouses,
    },
    places: {
      getter: getValuesPlaces,
      data: valuesPlaces,
    },
    placePosts: {
      getter: getValuesPlacePosts,
      data: valuesPlacePosts,
    },
    notes: {
      getter: getValuesNotes,
      data: valuesNotes,
    },
    tradePremises: {
      getter: getValuesTradePremises,
      data: valuesTradePremises,
    },
  };

  const getZDList = (selectedService: string) => {
    requests[selectedService].getter();
    return requests[selectedService].data;
  };

  useEffect(() => {
    setZDCurrentState(null);
    getZDList(selectedService);
  }, [selectedService]);

  useEffect(() => {
    const getterStr =
      selectedService[0].toUpperCase() + selectedService.substr(1) + "Getter";
    const currentGetter = requests[selectedService].getter;

    setZDCurrentState((p) => ({
      ...p,
      loading: false,
      items: get(
        requests[selectedService].data,
        `data.${getterStr}.getValues.items`,
        []
      ),
      total: get(
        requests[selectedService].data,
        `data.${getterStr}.getValues.total`,
        0
      ),
      // refetch для повторного запроса списка (notes, services и др.) после добавления/удаления файлов или смены Compliance
      refetch:
        typeof currentGetter === "function"
          ? () => currentGetter()
          : p?.refetch ?? defaultStateVal.refetch,
    }));
  }, [requests[selectedService].data.loading]);

  return ZDCurrentState ?? defaultStateVal;
};
